import React from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { CONTACT_EMAIL } from "@/config/site";

export const LegalLayout = ({ title, updated, lead, children, testId }) => (
  <main className="px-6 sm:px-8 pt-16 sm:pt-20 pb-24" data-testid={testId}>
    <div className="max-w-3xl mx-auto">
      <h1 className="font-fraunces font-extrabold text-4xl sm:text-5xl tracking-tight text-[#1F1F1C] leading-[1.06]">
        {title}
      </h1>
      <p className="mt-4 text-sm text-[#4A463F]">Last updated {updated}</p>
      {lead && (
        <p
          className="mt-8 bg-[#EADCC6] border border-[#1F1F1C]/10 rounded-2xl px-6 py-5 text-sm md:text-base font-semibold text-[#1F1F1C] leading-relaxed"
          data-testid={`${testId}-lead`}
        >
          {lead}
        </p>
      )}
      <div className="mt-12 space-y-10">{children}</div>
      <div className="mt-16 pt-8 border-t border-[#1F1F1C]/10 text-sm text-[#4A463F]">
        Questions about this page? Email{" "}
        <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#C8703A] font-semibold hover:underline underline-offset-4">{CONTACT_EMAIL}</a>{" "}
        or{" "}
        <Link to="/audit" className="text-[#C8703A] font-semibold hover:underline underline-offset-4">book a free sales audit</Link>.
      </div>
    </div>
  </main>
);

export const Clause = ({ heading, children, testId }) => (
  <section data-testid={testId}>
    <h2 className="font-fraunces font-extrabold text-base md:text-lg text-[#1F1F1C]">{heading}</h2>
    <div className="mt-3 space-y-3 text-sm md:text-base text-[#4A463F] leading-relaxed">{children}</div>
  </section>
);

export const Bullets = ({ items }) => (
  <ul className="space-y-2 pl-5 list-disc marker:text-[#C8703A]">
    {items.map((i) => (
      <li key={i}>{i}</li>
    ))}
  </ul>
);

const GUARANTEES = [
  "Your outbound engine goes live within 2 weeks of the first payment clearing and us having the access we need.",
  "Your list of 500 contacts is enriched and verified for email, phone and LinkedIn.",
  "Every workflow we build runs in your accounts, in your name, with a written guide your team keeps.",
];

export default function TermsPage() {
  return (
    <LegalLayout
      title="Terms of Service"
      updated="June 2026"
      lead="If you sign a service agreement with us, that agreement takes priority over these terms."
      testId="terms-page"
    >
      <Seo
        title="Terms of Service"
        description="The terms covering Raki Solutions sales audits, outbound engine builds, custom AI workflows, payments and what we do and do not guarantee in Singapore."
        path="/terms"
      />

      <Clause heading="1. Who we are" testId="terms-who-we-are">
        <p>
          Raki Solutions is a Singapore business that builds AI sales workflows and outbound engines
          for B2B companies. These terms apply to every service you buy from us. By accepting a
          proposal or paying an invoice, you agree to them.
        </p>
      </Clause>

      <Clause heading="2. The free sales audit" testId="terms-audit">
        <p>
          The sales workflow audit is free and carries no obligation. It covers a 30 minute call on
          how you get and handle leads today, followed by a one-page summary within 48 hours of the
          call.
        </p>
        <p>
          The summary is our opinion based on what you tell us and what we can see. It is advice, not
          a warranty of any result.
        </p>
      </Clause>

      <Clause heading="3. Outbound Engine Build" testId="terms-build">
        <p>
          The Outbound Engine Build is a fixed fee of S$3,000, covering one target customer profile
          and one market: the profile and buyer roles agreed in writing, an enriched list of 500
          contacts, sending setup with warm-up and deliverability checks, email and LinkedIn
          sequences, reply handling and meeting booking into your calendar and CRM, and a written
          guide to running the engine.
        </p>
        <p className="font-semibold text-[#1F1F1C]">
          The build is paid 50% to start and 50% on go-live. Each extra market is S$1,000.
        </p>
        <p>
          Our 2 week delivery window runs from the day the first payment clears and we have access to
          the accounts we need. Delays caused by waiting on access, approvals or content from your
          side extend that window by the same amount of time.
        </p>
      </Clause>

      <Clause heading="4. Add-ons" testId="terms-addons">
        <p>
          Coached handover is S$500. The support bundle, which covers one month of support with
          weekly check-ins and a coached handover, is S$1,000. SDR coaching is S$500 per session, or
          S$1,200 for three sessions.
        </p>
        <p>
          Sessions are scheduled with you. A session you do not attend, or cancel with less than 24
          hours notice, is treated as delivered, but we will always try to move a session rather than
          lose it.
        </p>
      </Clause>

      <Clause heading="5. Custom AI Sales Workflows" testId="terms-custom">
        <p>
          Custom work is scoped on a discovery call and quoted in a written proposal that lists each
          workflow with its trigger, steps and result, plus the timeline and the price. Work starts
          once you accept the proposal in writing.
        </p>
        <p>
          Anything outside the accepted proposal is quoted separately before we start it.
        </p>
      </Clause>

      <Clause heading="6. Tools and subscriptions" testId="terms-tools">
        <p>
          You pay for your own tool subscriptions directly, for example your CRM, sending tools, data
          providers, automation platforms and any AI models. We never resell or mark up tools, and we
          will tell you what the expected monthly cost looks like before anything is set up.
        </p>
        <p>
          Everything runs in your accounts, in your name. Tool pricing, availability and terms are set
          by those providers, not by us.
        </p>
      </Clause>

      <Clause heading="7. What we guarantee, and what we do not" testId="terms-guarantee">
        <p className="font-semibold text-[#1F1F1C]">
          We do not guarantee any number of meetings, replies, conversations, opportunities, pipeline
          value or revenue. Outcomes depend on your market, your offer, your pricing and your team.
        </p>
        <p>We do guarantee what is in our control:</p>
        <Bullets items={GUARANTEES} />
      </Clause>

      <Clause heading="8. Ownership" testId="terms-ownership">
        <p>
          Everything we build for you lives in your accounts and is yours: workflows, sequences,
          scripts, lists, CRM configuration and documentation. We keep ownership of our own general
          methods, templates and know-how, which we reuse across clients.
        </p>
      </Clause>

      <Clause heading="9. Payments, taxes and refunds" testId="terms-payments">
        <p>
          Fees are invoiced in Singapore dollars and due within the window stated on the invoice.
          Where tax applies, it is shown on the invoice.
        </p>
        <p>
          Because our services are delivered as skilled work over a short, fixed window, fees are not
          refundable once work has begun. If we have not started, tell us and we will refund you in
          full. Work may be paused while an invoice is overdue.
        </p>
      </Clause>

      <Clause heading="10. Referral program" testId="terms-referral">
        <p>
          Refer a company to us. If they sign, you get 10% of the full project value. Once they have
          paid in full, you receive 10% of the full project value within 14 days.
        </p>
        <p>
          The referral must reach us before the company contacts us themselves, and it applies to
          their first project only. Nothing is payable on a deal that is cancelled or refunded.
        </p>
      </Clause>

      <Clause heading="11. Confidentiality" testId="terms-confidentiality">
        <p>
          We treat your data, pricing, customer lists and commercial plans as confidential and use
          them only to deliver your service. We will sign your NDA on request.
        </p>
      </Clause>

      <Clause heading="12. Liability" testId="terms-liability">
        <p>
          We are liable only for direct losses we cause, capped at the fees you have paid us in the 12
          months before the claim. We are not liable for lost profit, lost pipeline, lost data held in
          third party tools, or for how third party tools and platforms behave, including
          deliverability, account suspensions and API changes.
        </p>
        <p>
          You are responsible for making sure your outreach complies with the laws that apply to the
          markets you sell into, including data protection and anti-spam rules. We will advise, but
          the final call on who you contact is yours.
        </p>
      </Clause>

      <Clause heading="13. Governing law" testId="terms-law">
        <p>
          These terms are governed by the laws of Singapore, and the courts of Singapore have
          exclusive jurisdiction over any dispute. We may update these terms from time to time, and
          the version in force is the one published here on the day you accept a proposal.
        </p>
      </Clause>
    </LegalLayout>
  );
}
